import React, { useState, useCallback, useEffect } from "react";
import { Upload, FileAudio, FileVideo, FileText, X, Loader2, AlertTriangle } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useUpload } from "@/context/UploadContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getApiBaseUrl } from "@/lib/config";
import axios from "axios";
import { toast } from "sonner";
import videoApi from "@/lib/api";

const SUPPORTED_MIME_TYPES = [
  "video/mp4",
  "video/quicktime",
  "video/x-msvideo",
  "video/webm",
  "video/x-matroska",
  "audio/mpeg",
  "audio/mp3",
  "audio/wav",
  "audio/x-wav",
  "audio/mp4",
  "audio/x-m4a",
  "audio/aac",
  "audio/ogg",
  "audio/flac",
  "audio/webm",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const SUPPORTED_EXTENSIONS = [
  ".mp4",
  ".mov",
  ".avi",
  ".webm",
  ".mkv",
  ".m4v",
  ".mp3",
  ".wav",
  ".m4a",
  ".aac",
  ".ogg",
  ".flac",
  ".pdf",
  ".docx",
];

const UNAMBIGUOUS_AUDIO_EXTENSIONS = [".mp3", ".wav", ".m4a", ".aac", ".ogg", ".flac"];
const DOCUMENT_EXTENSIONS = [".pdf", ".docx"];
const INPUT_ACCEPT = "video/*,audio/*,.mp4,.mov,.avi,.webm,.mkv,.m4v,.mp3,.wav,.m4a,.aac,.ogg,.flac,.pdf,.docx";
const MAX_DOCUMENT_UPLOAD_SIZE_BYTES = 300 * 1024 * 1024;
const MAX_MEDIA_UPLOAD_SIZE_BYTES = 5 * 1024 * 1024 * 1024;
const DEFAULT_RECORDING_TYPE = "otro";
const TRANSCRIPTION_LANGUAGES = [
  { id: "es", label_es: "Español", label_en: "Spanish" },
  { id: "en", label_es: "Inglés", label_en: "English" },
  { id: "auto", label_es: "Detectar automáticamente", label_en: "Detect automatically" },
];
// Mirrors the backend catalogue so the selector still works if the request fails.
const FALLBACK_RECORDING_TYPES = [
  { id: "audiencia_judicial", label_es: "Audiencia o juicio", label_en: "Court hearing or trial" },
  { id: "declaracion_testimonio", label_es: "Declaración o testimonio", label_en: "Statement or testimony" },
  { id: "reunion_asamblea", label_es: "Reunión o asamblea", label_en: "Meeting or assembly" },
  { id: "entrevista", label_es: "Entrevista", label_en: "Interview" },
  { id: "clase_conferencia", label_es: "Clase o conferencia", label_en: "Lecture or class" },
  { id: "llamada", label_es: "Llamada telefónica", label_en: "Phone call" },
  { id: "nota_voz", label_es: "Nota de voz o dictado", label_en: "Voice note or dictation" },
  { id: "podcast_video", label_es: "Podcast o video", label_en: "Podcast or video" },
  { id: "otro", label_es: "Otro / no estoy seguro", label_en: "Other / not sure" },
];

const getFileExtension = (candidateFile) => {
  const name = candidateFile?.name?.toLowerCase?.() || "";
  const dotIndex = name.lastIndexOf(".");
  return dotIndex >= 0 ? name.slice(dotIndex) : "";
};

const isAudioFile = (candidateFile) => {
  const mimeType = candidateFile?.type?.toLowerCase?.() || "";
  const extension = getFileExtension(candidateFile);
  if (mimeType.startsWith("audio/")) return true;
  if (mimeType.startsWith("video/")) return false;
  return UNAMBIGUOUS_AUDIO_EXTENSIONS.includes(extension);
};

const isDocumentFile = (candidateFile) => {
  const mimeType = candidateFile?.type?.toLowerCase?.() || "";
  const extension = getFileExtension(candidateFile);
  return mimeType === "application/pdf"
    || mimeType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    || DOCUMENT_EXTENSIONS.includes(extension);
};

const isSupportedFile = (candidateFile) => {
  if (!candidateFile) return false;
  const mimeType = candidateFile?.type?.toLowerCase?.() || "";
  const extension = getFileExtension(candidateFile);
  return SUPPORTED_MIME_TYPES.includes(mimeType) || SUPPORTED_EXTENSIONS.includes(extension);
};

const VideoUpload = ({ onUploadComplete }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [status, setStatus] = useState("idle");
  const { language, t } = useLanguage();
  const { startUpload, updateProgress, endUpload } = useUpload();
  const [transcriptionLanguage, setTranscriptionLanguage] = useState("");
  const [recordingType, setRecordingType] = useState(DEFAULT_RECORDING_TYPE);
  const [recordingTypes, setRecordingTypes] = useState(FALLBACK_RECORDING_TYPES);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${getApiBaseUrl()}/transcription/recording-types`)
      .then((response) => {
        const options = response?.data?.recording_types;
        if (!cancelled && Array.isArray(options) && options.length) {
          setRecordingTypes(options);
        }
      })
      .catch(() => {
        // Keep the bundled fallback list; this selector must never block an upload.
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const documentsEnabled = true;
  const supportedExtensions = documentsEnabled
    ? [".MP4", ".MOV", ".AVI", ".MKV", ".WEBM", ".MP3", ".WAV", ".M4A", ".AAC", ".OGG", ".FLAC", ".PDF", ".DOCX"]
    : [".MP4", ".MOV", ".AVI", ".MKV", ".WEBM", ".MP3", ".WAV", ".M4A", ".AAC", ".OGG", ".FLAC"];
  const inputAccept = documentsEnabled
    ? INPUT_ACCEPT
    : "video/*,audio/*,.mp4,.mov,.avi,.webm,.mkv,.m4v,.mp3,.wav,.m4a,.aac,.ogg,.flac";

  const invalidTypeMessage = language === "es"
    ? (documentsEnabled
        ? "Tipo de archivo inválido. Soportados: MP4, MOV, AVI, WebM, MKV, MP3, WAV, M4A, AAC, OGG, FLAC, PDF y DOCX"
        : "Tipo de archivo inválido. Soportados: MP4, MOV, AVI, WebM, MKV, MP3, WAV, M4A, AAC, OGG y FLAC")
    : (documentsEnabled
        ? "Invalid file type. Supported: MP4, MOV, AVI, WebM, MKV, MP3, WAV, M4A, AAC, OGG, FLAC, PDF, and DOCX"
        : "Invalid file type. Supported: MP4, MOV, AVI, WebM, MKV, MP3, WAV, M4A, AAC, OGG, and FLAC");

  const documentTooLargeMessage = language === "es"
    ? "Los documentos PDF y Word pueden pesar hasta 300 MB."
    : "PDF and Word documents can be up to 300 MB.";

  const mediaTooLargeMessage = language === "es"
    ? "Los archivos de audio y video pueden pesar hasta 5 GB."
    : "Audio and video files can be up to 5 GB.";

  const validateCandidateFile = useCallback((candidateFile) => {
    if (!candidateFile) return false;
    if (!isSupportedFile(candidateFile)) {
      toast.error(invalidTypeMessage);
      return false;
    }
    if (isDocumentFile(candidateFile) && Number(candidateFile.size || 0) > MAX_DOCUMENT_UPLOAD_SIZE_BYTES) {
      toast.error(documentTooLargeMessage);
      return false;
    }
    if (!isDocumentFile(candidateFile) && Number(candidateFile.size || 0) > MAX_MEDIA_UPLOAD_SIZE_BYTES) {
      toast.error(mediaTooLargeMessage);
      return false;
    }
    return true;
  }, [documentTooLargeMessage, invalidTypeMessage, mediaTooLargeMessage]);

  useEffect(() => {
    if (uploading) {
      const handleBeforeUnload = (e) => {
        e.preventDefault();
        e.returnValue = language === "es"
          ? "La subida está en progreso. ¿Estás seguro de que quieres salir?"
          : "Upload in progress. Are you sure you want to leave?";
        return e.returnValue;
      };

      window.addEventListener("beforeunload", handleBeforeUnload);

      toast.warning(
        language === "es"
          ? "⚠️ No navegues a otra página mientras se sube el archivo"
          : "⚠️ Do not navigate away while the file is uploading",
        { duration: Infinity, id: "upload-warning" }
      );

      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
        toast.dismiss("upload-warning");
      };
    }
  }, [uploading, language]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (validateCandidateFile(droppedFile)) {
      setFile(droppedFile);
    }
  }, [validateCandidateFile]);

  const handleFileSelect = useCallback((e) => {
    const selectedFile = e.target.files[0];
    if (validateCandidateFile(selectedFile)) {
      setFile(selectedFile);
    }
  }, [validateCandidateFile]);

  const getStatusText = () => {
    const statusTexts = {
      initializing: language === "es" ? "Preparando tu archivo..." : "Preparing your file...",
      uploading: language === "es" ? "Subiendo tu archivo..." : "Uploading your file...",
      finalizing: language === "es" ? "Finalizando la subida..." : "Finalizing upload...",
      processing: language === "es" ? "Procesando tu archivo..." : "Processing your file...",
    };
    return statusTexts[status] || `${uploadProgress}%`;
  };

  const handleUpload = async () => {
    if (!file) return;

    const uploadedFile = file;
    const isDocument = isDocumentFile(uploadedFile);

    if (!isDocument && !transcriptionLanguage) {
      toast.error(
        language === "es"
          ? "Selecciona el idioma del audio antes de continuar."
          : "Select the audio language before continuing."
      );
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setStatus("initializing");
    startUpload(uploadedFile.name);

    try {
      const result = await videoApi.upload(
        uploadedFile,
        isDocument ? "auto" : transcriptionLanguage,
        (progress) => {
          setUploadProgress(progress);
          updateProgress(progress);
        },
        (newStatus) => {
          setStatus(newStatus);
        },
        { recordingType }
      );

      toast.success(
        isDocumentFile(uploadedFile)
          ? (language === "es"
              ? "¡Documento subido! Lo analizaremos en segundo plano."
              : "Document uploaded! We’ll analyze it in the background.")
          : (language === "es"
              ? "¡Archivo subido! Lo procesaremos en segundo plano."
              : "Upload complete! We’ll process it in the background.")
      );

      setFile(null);
      setUploadProgress(0);
      setStatus("idle");
      endUpload();

      if (onUploadComplete) {
        onUploadComplete(result, uploadedFile);
      }
    } catch (error) {
      console.error("Upload error:", error);
      endUpload();
      toast.error(
        error?.response?.data?.detail || error?.message || (
          language === "es"
            ? "Error al subir el archivo. Por favor, inténtalo de nuevo."
            : "Failed to upload file. Please try again."
        )
      );
    } finally {
      setUploading(false);
    }
  };

  const clearFile = () => {
    setFile(null);
    setUploadProgress(0);
    setStatus("idle");
    setTranscriptionLanguage("");
    setRecordingType(DEFAULT_RECORDING_TYPE);
  };

  const formatFileSize = (bytes) => {
    if (bytes >= 1024 * 1024 * 1024) {
      return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const selectedFileIsAudio = isAudioFile(file);
  const selectedFileIsDocument = isDocumentFile(file);
  const selectedFileTypeLabel = selectedFileIsDocument
    ? (language === "es" ? "Documento" : "Document")
    : (selectedFileIsAudio ? "Audio" : "Video");

  return (
    <div
      className={`drop-zone rounded-lg p-8 md:p-12 flex flex-col items-center justify-center text-center ${
        isDragging ? "drag-over" : ""
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      data-testid="video-upload-zone"
    >
      {!file ? (
        <>
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <Upload className="w-8 h-8 text-primary" />
          </div>
          <h3 className="text-xl font-semibold mb-2">
            {documentsEnabled
              ? (language === "es" ? "Arrastra tu video, audio o documento aquí" : "Drop your video, audio, or document here")
              : (language === "es" ? "Arrastra tu video o audio aquí" : "Drop your video or audio here")}
          </h3>
          <p className="text-muted-foreground text-sm mb-4">{t("orClickToBrowse")}</p>
          <p className="text-muted-foreground text-xs mb-6">
            {language === "es"
              ? (documentsEnabled
                  ? "Audio y video suben directo a cloud con reintentos seguros. Los archivos grandes se envían por partes sin alterar el contenido; los documentos PDF y Word admiten hasta 300 MB y siguen su flujo de análisis."
                  : "Audio y video suben directo a cloud con reintentos seguros y multipart para archivos grandes.")
              : (documentsEnabled
                  ? "Audio and video upload directly to cloud with safe retries. Large files use multipart without altering the media; PDF and Word documents support up to 300 MB and keep their analysis flow."
                  : "Audio and video upload directly to cloud with safe retries and multipart for large files.")}
          </p>
          <div className="flex flex-wrap gap-2 justify-center mb-6">
            {supportedExtensions.map((ext) => (
              <span
                key={ext}
                className="px-3 py-1 text-xs font-mono bg-muted rounded-full text-muted-foreground"
              >
                {ext}
              </span>
            ))}
          </div>
          <label htmlFor="file-upload">
            <Button
              asChild
              className="bg-primary text-primary-foreground hover:bg-primary/90 btn-glow cursor-pointer"
              data-testid="select-video-btn"
            >
              <span>
                <FileVideo className="w-4 h-4 mr-2" />
                {language === "es" ? "Seleccionar archivo" : "Select file"}
              </span>
            </Button>
          </label>
          <input
            id="file-upload"
            type="file"
            accept={inputAccept}
            onChange={handleFileSelect}
            className="hidden"
          />
        </>
      ) : (
        <div className="w-full max-w-md">
          <div className="flex items-center gap-4 mb-6 p-4 bg-card rounded-lg border border-border">
            {selectedFileIsAudio ? (
              <FileAudio className="w-10 h-10 text-primary shrink-0" />
            ) : selectedFileIsDocument ? (
              <FileText className="w-10 h-10 text-primary shrink-0" />
            ) : (
              <FileVideo className="w-10 h-10 text-primary shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{file.name}</p>
              <p className="text-sm text-muted-foreground">
                {formatFileSize(file.size)} • {selectedFileTypeLabel}
              </p>
            </div>
            {!uploading && (
              <Button
                variant="ghost"
                size="icon"
                onClick={clearFile}
                data-testid="clear-file-btn"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>

          {!uploading && !selectedFileIsDocument && (
            <div className="mb-4 text-left" data-testid="transcription-language-field">
              <label className="block text-sm font-medium mb-2" htmlFor="transcription-language-select">
                {language === "es" ? "Idioma del audio" : "Audio language"}
                <span className="text-destructive ml-1">*</span>
              </label>
              <Select value={transcriptionLanguage} onValueChange={setTranscriptionLanguage}>
                <SelectTrigger id="transcription-language-select" data-testid="transcription-language-select">
                  <SelectValue
                    placeholder={language === "es" ? "Selecciona un idioma" : "Select a language"}
                  />
                </SelectTrigger>
                <SelectContent>
                  {TRANSCRIPTION_LANGUAGES.map((option) => (
                    <SelectItem key={option.id} value={option.id} data-testid={`transcription-language-option-${option.id}`}>
                      {language === "es" ? option.label_es : option.label_en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!transcriptionLanguage && (
                <p className="mt-2 text-xs text-destructive" data-testid="transcription-language-required-hint">
                  {language === "es"
                    ? "Selecciona un idioma para poder iniciar la transcripción."
                    : "Select a language to start the transcription."}
                </p>
              )}
            </div>
          )}

          {!uploading && !selectedFileIsDocument && (
            <div className="mb-6 text-left" data-testid="recording-type-field">
              <label className="block text-sm font-medium mb-2" htmlFor="recording-type-select">
                {language === "es" ? "¿Qué tipo de grabación es?" : "What kind of recording is this?"}
              </label>
              <Select value={recordingType} onValueChange={setRecordingType}>
                <SelectTrigger id="recording-type-select" data-testid="recording-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {recordingTypes.map((option) => (
                    <SelectItem key={option.id} value={option.id} data-testid={`recording-type-option-${option.id}`}>
                      {language === "es" ? option.label_es : option.label_en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-2 text-xs text-muted-foreground">
                {language === "es"
                  ? "Nos ayuda a reconocer mejor el vocabulario propio de ese tipo de audio."
                  : "This helps us recognise the vocabulary typical of that kind of audio."}
              </p>
            </div>
          )}

          {uploading ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-yellow-500 text-sm" data-testid="upload-warning-banner">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>
                  {language === "es"
                    ? "No cierres ni navegues fuera de esta página"
                    : "Do not close or navigate away from this page"}
                </span>
              </div>
              <div className="flex items-center gap-3 justify-center text-sm text-muted-foreground" data-testid="upload-status-row">
                <Loader2 className="w-4 h-4 animate-spin text-primary" />
                <span data-testid="upload-status-label">{getStatusText()}</span>
              </div>
              <Progress value={uploadProgress} className="h-2" />
              <p className="text-xs text-muted-foreground text-center" data-testid="upload-status-note">
                {selectedFileIsDocument
                  ? (language === "es"
                      ? "Extraeremos el texto, generaremos el resumen, los key moments por fragmento y activaremos el chat sobre el documento."
                      : "We’ll extract the text, generate the summary, create fragment-based key moments, and enable AI chat for the document.")
                  : (language === "es"
                      ? "Tu archivo sube directo a cloud; si es grande, Voxara lo envía por partes para evitar reinicios completos sin tocar audio ni video."
                      : "Your file uploads directly to cloud; if it is large, Voxara sends it in parts to avoid full restarts without touching the audio or video.")}
              </p>
            </div>
          ) : (
            <Button
              onClick={handleUpload}
              disabled={!selectedFileIsDocument && !transcriptionLanguage}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 btn-glow disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="upload-video-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              {selectedFileIsDocument
                ? (language === "es" ? "Subir y analizar" : "Upload & analyze")
                : (language === "es" ? "Subir y transcribir" : "Upload & transcribe")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default VideoUpload;
